import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { LoaderIcon, MapPinIcon } from 'lucide-react';
import { ManualEntry, Product } from '../types/catalog';
import { products } from '../data/products';
import { destinations } from '../data/destinations';
import { priceListSources } from '../data/quarries';
import { DEFAULT_HAUL_RATE, formatCurrency } from '../utils/geo';
import { useDestination } from '../hooks/useDestination';
import { SortKey, buildQuotes } from '../utils/quotes';
import { SearchBar } from '../components/SearchBar';
import { QuoteResults } from '../components/QuoteResults';
import { ResultActions } from '../components/ResultActions';
import { ManualPriceForm } from '../components/ManualPriceForm';
import { PendingSourcesPanel } from '../components/PendingSourcesPanel';
import { CatalogBrowser } from '../components/catalog/CatalogBrowser';
const quickPicks = ['p-62011', 'p-62010', 'p-52010', 'p-65016', 'p-65011'];
interface RealRatesProps {
  /** Haul radius, in miles, measured from the delivery address. */
  radiusMiles?: number;
  /** Estimated trucking rate, in dollars per loaded mile. */
  haulRatePerMile?: number;
  /** Load size priced out on every row, in tons or cubic yards. */
  loadUnits?: number;
}

/**
 * Internal supplier price lookup. Renders standalone — no site chrome — and
 * tells search engines to stay out, since it exposes cost basis.
 */
export function RealRates({
  radiusMiles = 30,
  haulRatePerMile = DEFAULT_HAUL_RATE,
  loadUnits = 15
}: RealRatesProps) {
  const [productQuery, setProductQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [destinationQuery, setDestinationQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('price');
  const [showAll, setShowAll] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>([]);
  const {
    destination,
    status
  } = useDestination(destinationQuery);
  const result = useMemo(() => selectedProduct && destination ? buildQuotes(selectedProduct, destination, radiusMiles, sortBy, {
    manualEntries,
    includeOutsideRadius: showAll,
    haulRatePerMile
  }) : {
    quotes: [],
    outsideRadius: 0
  }, [selectedProduct, destination, radiusMiles, sortBy, manualEntries, showAll, haulRatePerMile]);
  function handleSelect(product: Product) {
    setSelectedProduct(product);
    setProductQuery(product.name);
    setAddOpen(false);
  }
  function handleStartOver() {
    setSelectedProduct(null);
    setProductQuery('');
    setDestinationQuery('');
    setSortBy('price');
    setShowAll(false);
    setAddOpen(false);
  }
  function handleAddEntry(entry: ManualEntry) {
    setManualEntries(entries => [...entries, entry]);
    setAddOpen(false);
  }
  function handleRemoveEntry(entryId: string) {
    setManualEntries(entries => entries.filter(entry => entry.id !== entryId));
  }
  return <div className="flex min-h-full w-full flex-col bg-canvas" data-id="element-194" data-mp="2qhxs9tppc23o">
      <header className="border-b border-line bg-surface pt-[env(safe-area-inset-top)]" data-id="element-195" data-mp="2mhx6fo09woml">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-6 py-3" data-id="element-196" data-mp="309244kk2z6ua">
          <p className="text-sm font-semibold tracking-tight text-ink" data-id="element-197" data-mp="2va8tr103bvyc">
            Real Rates
            <span className="ml-2 font-normal text-ink-soft" data-id="element-198" data-mp="27k4okp797o58">internal price lookup</span>
          </p>
          <p className="tnum text-xs text-ink-soft" data-id="element-199" data-mp="23j2oy79fda2q">
            {priceListSources.length} price lists · plants within {radiusMiles} mi of the
            delivery address
          </p>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-6" data-id="element-200" data-mp="2cj7vygapi6gd">
        {selectedProduct ? <div className="py-8" data-id="element-201" data-mp="3sx7r7dylx7xa">
            <div className="pb-8" data-id="element-202" data-mp="3sw64udc54z09">
              <SearchBar productQuery={productQuery} onProductQueryChange={value => {
            setProductQuery(value);
            if (!value.trim()) setSelectedProduct(null);
          }} onSelectProduct={handleSelect} destinationQuery={destinationQuery} onDestinationChange={setDestinationQuery} compact data-id="element-203" data-mp="3szk9js8a1kav" />
            </div>

            {destination ? <>
                <QuoteResults product={selectedProduct} destination={destination} quotes={result.quotes} outsideRadius={result.outsideRadius} radiusMiles={radiusMiles} showAll={showAll} loadUnits={loadUnits} sortBy={sortBy} onSortChange={setSortBy} onRemoveManual={handleRemoveEntry} data-id="element-204" data-mp="2ggemlq1vtno3" />
                <p className="tnum px-1 pt-1 text-xs text-ink-soft" data-id="element-205" data-mp="2z4tgqm7hxht4">
                  {destination.method === 'address' ? `Measured from ${destination.label} — exact address` : destination.method === 'zip' ? `Measured from the center of ZIP ${destination.zip}. Add a street number for exact distance.` : `Measured from the center of ${destination.label}. Add a ZIP or street address for exact distance.`}
                </p>
              </> : <div className="rounded-xl border border-line bg-surface px-6 py-14 text-center" data-id="element-206" data-mp="1b9deuzil0hpv">
                {status === 'searching' ? <LoaderIcon className="mx-auto mb-3 h-6 w-6 animate-spin text-ink-soft" aria-hidden="true" data-id="element-207" data-mp="178bnf4uwp9yg" /> : <MapPinIcon className="mx-auto mb-3 h-6 w-6 text-ink-soft" aria-hidden="true" data-id="element-208" data-mp="xh78u6957r45" />}
                <p className="text-base font-medium text-ink" data-id="element-209" data-mp="1uz7eu0vga356">
                  {status === 'searching' ? 'Looking up that address…' : status === 'error' ? "Couldn't reach the address lookup" : status === 'notFound' ? "Couldn't find that address" : 'Add a delivery address'}
                </p>
                <p className="mx-auto mt-1 max-w-md text-sm text-ink-muted" data-id="element-210" data-mp="24bod2xj2xmoq">
                  {status === 'error' ? 'Enter a ZIP code instead — those resolve without a connection.' : `Type a street address for exact distance, or a city or ZIP to measure from
                       its center. Plants are ranked by distance from the delivery site.`}
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2" data-id="element-211" data-mp="1lcirbu6j2t33">
                  {destinations.slice(0, 6).map(option => <button key={option.zip} type="button" onClick={() => setDestinationQuery(`${option.label} ${option.zip}`)} className="rounded-full border border-line px-3 py-1.5 text-sm text-ink-muted transition-colors duration-150 hover:border-accent hover:text-accent" data-id="element-212" data-mp="3bndl3aqpbuya">
                      {option.label}
                    </button>)}
                </div>
              </div>}

            <ResultActions showAll={showAll} onToggleShowAll={() => setShowAll(value => !value)} onStartOver={handleStartOver} onAddPrice={() => setAddOpen(value => !value)} addOpen={addOpen} radiusMiles={radiusMiles} hiddenCount={result.outsideRadius} data-id="element-213" data-mp="1nrp7w0lbbxly" />

            {addOpen && <ManualPriceForm product={selectedProduct} onAdd={handleAddEntry} onCancel={() => setAddOpen(false)} data-id="element-214" data-mp="2ee6enuo991p" />}
          </div> : <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex min-h-[70vh] flex-col items-center justify-center py-16" data-id="element-215" data-mp="38piz05q20vie">
            <h1 className="text-center text-3xl font-semibold tracking-tight text-ink md:text-4xl" data-id="element-216" data-mp="3mgecxwfeu2su">
              What material?
            </h1>
            <p className="mt-2 text-center text-base text-ink-muted" data-id="element-217" data-mp="2prgikfbko6w7">
              Plant prices within {radiusMiles} miles of your delivery address, cheapest first.
            </p>

            <div className="mt-8 w-full max-w-3xl" data-id="element-218" data-mp="2yqk0vh2x0i9n">
              <SearchBar productQuery={productQuery} onProductQueryChange={setProductQuery} onSelectProduct={handleSelect} destinationQuery={destinationQuery} onDestinationChange={setDestinationQuery} compact={false} data-id="element-219" data-mp="2yt69zl5gf5tz" />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2" data-id="element-220" data-mp="23hzri8kvut7i">
              <span className="text-sm text-ink-soft" data-id="element-221" data-mp="1yi3l62opqpos">Frequently pulled:</span>
              {quickPicks.map(id => {
            const product = products.find(p => p.id === id);
            if (!product) return null;
            return <button key={id} type="button" onClick={() => handleSelect(product)} className="rounded-full border border-line bg-surface px-3 py-1.5 text-sm text-ink-muted transition-colors duration-150 hover:border-accent hover:text-accent" data-id="element-222" data-mp="1oqr4864gdjxj">
                    {product.name}
                  </button>;
          })}
            </div>

            <div className="mt-10 w-full max-w-3xl" data-id="element-223" data-mp="3560qj90sgtbl">
              <CatalogBrowser onCompare={handleSelect} data-id="element-224" data-mp="354plz6zirhjf" />
            </div>

            <div className="mt-4 w-full max-w-3xl" data-id="element-225" data-mp="308s611s64d76">
              <PendingSourcesPanel data-id="element-226" data-mp="1puhvlt0pag4u" />
            </div>

            {manualEntries.length > 0 && <p className="tnum mt-6 text-sm text-ink-soft" data-id="element-227" data-mp="1kwg3kilar6h8">
                {manualEntries.length} manually entered price
                {manualEntries.length === 1 ? '' : 's'} saved this session.
              </p>}
          </motion.div>}
      </main>

      <footer className="mx-auto w-full max-w-5xl border-t border-line px-6 pt-4 pb-[calc(2rem+env(safe-area-inset-bottom))]" data-id="element-228" data-mp="174jeyoiu7epl">
        <h2 className="text-xs font-medium uppercase tracking-wide text-ink-soft" data-id="element-229" data-mp="12683yagt2y5s">Sources</h2>
        <ul className="mt-2 space-y-1" data-id="element-230" data-mp="1guuwxezcvofh">
          {priceListSources.map(source => <li key={source.id} className="text-xs text-ink-soft" data-id="element-231" data-mp="1c3wbk0zig05d">
              <span className="text-ink-muted" data-id="element-232" data-mp="1c1jml3vc4pit">{source.label}</span> — {source.detail}
            </li>)}
        </ul>
        <p className="mt-2 text-xs text-ink-soft" data-id="element-233" data-mp="jf76xrz8xcov">
          Prices exclude WA sales tax and each seller's terms differ: Kangley and Ryterra are
          pick-up at the scale (+$4.15/ton surcharges at Kangley, $45 load minimum at Ryterra),
          Palmer is u-haul with pickup and dumptruck rates, and Pacific Topsoils quotes a
          delivered price per cubic yard plus a per-load delivery fee. Haul on pick-up rows is an
          estimate at {formatCurrency(haulRatePerMile)} per loaded mile.
        </p>
      </footer>
    </div>;
}